import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  DisplayProps,
  HeightProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  color,
  display,
  height as heightUtil,
  space,
  themeGet,
  width,
} from 'styled-system';

import Icon from 'common/components/Icon';
import { BodyBoldBB2, SubtitleST4 } from 'common/components/Typography';
import { useIsObserved } from 'common/hooks/intersection-observer';
import { useLocalization } from 'common/providers/LocaleProvider';
import { focusOutlineCSSWithInwardOffset } from 'common/theme/styles';
import type { ColorProps, OutlineProps, SpaceProps } from 'common/theme/system-types';
import type { BoxSizingProps, TextDecorationProps } from 'common/theme/system-utilities';
import { boxSizing, outline, textDecoration } from 'common/theme/system-utilities';
import CarouselNavigation, {
  ListItem,
  listItemStyles,
} from '../../list-navigation/components/CarouselNavigation';
import { useRelatedSearchTracking } from '../hooks/use-related-search-tracking';

export interface RelatedSearchItem {
  query: string;
}

export enum RelatedSearchPlacement {
  SearchHeader = 'search-header',
  PlpInline = 'plp-inline',
}

interface RelatedSearchProps {
  items: RelatedSearchItem[];
  placement: RelatedSearchPlacement;
}

const Chip = styled.a<
  DisplayProps &
    BackgroundColorProps &
    ColorProps &
    TextDecorationProps &
    BorderProps &
    BorderColorProps &
    BorderRadiusProps &
    AlignItemsProps &
    SpaceProps &
    HeightProps &
    BoxSizingProps &
    OutlineProps
>`
  ${display};
  ${backgroundColor};
  ${color};
  ${textDecoration};
  ${border};
  ${borderColor};
  ${borderRadius};
  ${alignItems};
  ${space};
  ${heightUtil};
  ${boxSizing};
  ${outline};

  &:hover,
  &:focus {
    color: ${themeGet('colors.sys.neutral.text.hover')};
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:focus-visible {
    ${focusOutlineCSSWithInwardOffset};
  }
`;

const chipStyles: ComponentProps<typeof Chip> = {
  display: 'inline-flex',
  backgroundColor: 'sys.neutral.background.default',
  color: 'sys.neutral.text.default',
  textDecoration: 'none',
  border: '1px solid',
  borderColor: 'sys.neutral.border.weak',
  borderRadius: 'medium',
  alignItems: 'center',
  pl: 'sp_8',
  pr: 'sp_12',
  boxSizing: 'border-box',
  height: '32px',
  mr: 'sp_8',
  outline: 'none',
};

const Section = styled.section<WidthProps & DisplayProps>`
  ${width};
  ${display};
`;

const ICON_SIZE = 14;

const StyledCarouselNavigation = styled(CarouselNavigation)<SpaceProps>`
  ${space};
`;

interface RelatedSearchChipProps {
  item: RelatedSearchItem;
  onObserve: () => void;
  onClick: () => void;
}

const RelatedSearchChip = ({ item, onObserve, onClick }: RelatedSearchChipProps) => {
  const [ref, isObserved] = useIsObserved({ threshold: 1 });

  useEffect(() => {
    if (isObserved) {
      onObserve();
    }
  }, [isObserved, onObserve]);

  return (
    <Chip
      {...chipStyles}
      href={`/search?${new URLSearchParams({ query: item.query })}`}
      onClick={onClick}
      ref={ref}
      data-testid="related-search-chip"
      data-cnstrc-related-search-term={item.query}
    >
      <Icon name="search" size={ICON_SIZE} styles={{ mr: 'sp_8' }} />
      <SubtitleST4 as="span" color="inherit">
        {item.query}
      </SubtitleST4>
    </Chip>
  );
};

const RelatedSearch = ({ items, placement }: RelatedSearchProps) => {
  const { handleObserve, handleClick } = useRelatedSearchTracking(items, placement);
  const { t } = useLocalization();

  if (!items.length) {
    return null;
  }

  const isPlpInlinePlacement = placement === RelatedSearchPlacement.PlpInline;

  return (
    <Section
      data-corgi-id="related-search"
      data-cnstrc-related-searches
      width="100%"
      display="grid"
    >
      {isPlpInlinePlacement ? (
        <BodyBoldBB2 ml={['sp_16', null, 'sp_0']} mb={['sp_8', null, null, 'sp_12']}>
          {t('h24_related_search_title')}
        </BodyBoldBB2>
      ) : (
        <SubtitleST4
          as="p"
          mt="sp_0"
          mb={['sp_8', null, null, 'sp_12']}
          ml={['sp_16', 'sp_24', 'sp_0']}
          color="sys.neutral.text.default"
        >
          {t('h24_related_search_short_title')}
        </SubtitleST4>
      )}

      <StyledCarouselNavigation lightArrow data-corgi-id="related-search-carousel" py="sp_2">
        {items.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === items.length - 1;

          return (
            <ListItem
              key={item.query}
              {...listItemStyles}
              {...(isFirst && {
                ml: isPlpInlinePlacement ? ['sp_16', null, 'sp_0'] : ['sp_16', 'sp_24', 'sp_0'],
              })}
              {...(isLast && {
                mr: isPlpInlinePlacement ? ['sp_4', 'sp_12', 'sp_18'] : ['sp_4', 'sp_12', 'sp_0'],
              })}
            >
              <RelatedSearchChip
                item={item}
                onObserve={() => handleObserve(index)}
                onClick={() => handleClick(item, index)}
              />
            </ListItem>
          );
        })}
      </StyledCarouselNavigation>
    </Section>
  );
};

export default RelatedSearch;
